import type { TFunction } from 'i18next';
import type { BrandOption } from '@/features/completeness/completeness.api';

const whitespaceNameKeys: Record<string, string> = {
  'U+0009': 'tab',
  'U+000A': 'lineFeed',
  'U+000B': 'verticalTab',
  'U+000C': 'formFeed',
  'U+000D': 'carriageReturn',
  'U+0020': 'space',
  'U+00A0': 'nonBreakingSpace',
  'U+1680': 'oghamSpaceMark',
  'U+2000': 'enQuad',
  'U+2001': 'emQuad',
  'U+2002': 'enSpace',
  'U+2003': 'emSpace',
  'U+2004': 'threePerEmSpace',
  'U+2005': 'fourPerEmSpace',
  'U+2006': 'sixPerEmSpace',
  'U+2007': 'figureSpace',
  'U+2008': 'punctuationSpace',
  'U+2009': 'thinSpace',
  'U+200A': 'hairSpace',
  'U+2028': 'lineSeparator',
  'U+2029': 'paragraphSeparator',
  'U+202F': 'narrowNonBreakingSpace',
  'U+205F': 'mediumMathematicalSpace',
  'U+3000': 'ideographicSpace',
  'U+FEFF': 'zeroWidthNoBreakSpace',
};

function formatWhitespaceRuns(
  runs: NonNullable<BrandOption['boundaryWhitespace']>['leading'],
  t: TFunction,
): string {
  return runs.map((run) => t('dashboard.completeness.boundaryWhitespace.run', {
    whitespace: t(`dashboard.completeness.boundaryWhitespace.${whitespaceNameKeys[run.codePoint] ?? 'unicodeWhitespace'}`, { codePoint: run.codePoint }),
    count: run.count,
  })).join(', ');
}

/** Returns the locale-specific, visible and accessible selector/overview label. */
export function formatBrandDisplayName(brand: BrandOption, t: TFunction): string {
  const boundaryWhitespace = brand.boundaryWhitespace;
  if (!boundaryWhitespace || (boundaryWhitespace.leading.length === 0 && boundaryWhitespace.trailing.length === 0)) {
    return brand.name;
  }

  const boundaries = [
    boundaryWhitespace.leading.length > 0 && t('dashboard.completeness.boundaryWhitespace.leading', {
      whitespace: formatWhitespaceRuns(boundaryWhitespace.leading, t),
    }),
    boundaryWhitespace.trailing.length > 0 && t('dashboard.completeness.boundaryWhitespace.trailing', {
      whitespace: formatWhitespaceRuns(boundaryWhitespace.trailing, t),
    }),
  ].filter((value): value is string => Boolean(value));

  return t('dashboard.completeness.boundaryWhitespace.label', {
    name: brand.name,
    boundaries: boundaries.join('; '),
  });
}
